import type { Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { Workflow } from '@awork/features/workflow/models/workflow.model'
import { WorkflowStore } from '@awork/features/workflow/state/workflow.store'
import { Order } from '@awork/core/state/signal-store/types'

@Injectable({ providedIn: 'root' })
export class WorkflowQuery extends EntitySignalQuery<Workflow> {
  protected store: WorkflowStore

  constructor() {
    const store = inject(WorkflowStore)

    super(store)

    this.store = store
  }

  /**
   * Gets all workflows of the current workspace.
   * Connected external workflows are excluded from this query as a frontend safeguard.
   * @param {(workflow: Workflow) => boolean} filterBy Optional filter function to apply to workflows
   * @returns {Signal<Workflow[]>}
   */
  queryWorkflows(filterBy?: (workflow: Workflow) => boolean): Signal<Workflow[]> {
    return computed(() => this.getWorkflows(filterBy))
  }

  /**
   * Gets a workflow by id
   * @param {string} workflowId
   * @returns {Signal<Workflow>}
   */
  queryWorkflow(workflowId: string): Signal<Workflow> {
    return computed(() => this.getWorkflowById(workflowId))
  }

  /**
   * Gets all active (non-archived) workflows of the current workspace.
   * @returns {Signal<Workflow[]>}
   */
  queryActiveWorkflows(): Signal<Workflow[]> {
    return computed(() => this.getActiveWorkflows())
  }

  /**
   * Gets all archived workflows of the current workspace.
   * @returns {Signal<Workflow[]>}
   */
  queryArchivedWorkflows(): Signal<Workflow[]> {
    return computed(() => this.getArchivedWorkflows())
  }

  /**
   * Gets all workflows that belong to connected external projects.
   * @returns {Signal<Workflow[]>}
   */
  queryExternalWorkflows(): Signal<Workflow[]> {
    return computed(() => this.getExternalWorkflows())
  }

  /**
   * Gets all current-workspace workflows sorted by name and filtered with an optional predicate.
   * @param {(workflow: Workflow) => boolean} filterBy
   * @returns {Workflow[]}
   */
  private getWorkflows(filterBy?: (workflow: Workflow) => boolean): Workflow[] {
    const workflows = this.queryAll({
      filterBy: workflow => this.isLocalWorkflow(workflow) && (!filterBy || filterBy(workflow)),
      sortBy: 'name',
      sortByOrder: Order.ASC
    })

    return workflows()
  }

  /**
   * Gets a workflow by id.
   * @param {string} workflowId
   * @returns {Workflow}
   */
  getWorkflowById(workflowId: string): Workflow {
    const workflow = this.queryEntity(workflowId)

    return workflow()
  }

  /**
   * Gets all active current-workspace workflows.
   * @returns {Workflow[]}
   */
  private getActiveWorkflows(): Workflow[] {
    return this.getWorkflows().filter(workflow => !workflow.isArchived)
  }

  /**
   * Gets all archived current-workspace workflows.
   * @returns {Workflow[]}
   */
  private getArchivedWorkflows(): Workflow[] {
    return this.getWorkflows().filter(workflow => workflow.isArchived)
  }

  /**
   * Gets all workflows that belong to connected external projects.
   * @returns {Workflow[]}
   */
  private getExternalWorkflows(): Workflow[] {
    const workflows = this.queryAll({
      filterBy: workflow => !this.isLocalWorkflow(workflow),
      sortBy: 'name',
      sortByOrder: Order.ASC
    })

    return workflows()
  }

  /**
   * Checks whether a workflow belongs to the current workspace workflow set.
   * @param {Workflow} workflow
   * @returns {boolean}
   */
  private isLocalWorkflow(workflow: Workflow): boolean {
    return !workflow.isExternal
  }
}
